import React, { useState, useEffect, useRef } from 'react';

const AC  = '#6C63FF';
const AC2 = '#8A8DFF';
const T = {
  bg: '#FBFAF6', card: '#fff', border: '#ECE7DB', text: '#1B1B23',
  muted: '#57606A', dim: '#A39C8B', fdim: '#E3E0D8',
  inputBg: '#F4F2FB', inputBorder: '#DAD5E8',
  lumiCard: '#F2F1FB', lumiBorder: '#E2DDF2',
  hf: "'Hanken Grotesk', sans-serif",
  fd: "'Space Grotesk', sans-serif",
  fm: "'Space Mono', monospace",
};

const SCREENS = [
  { eyebrow: 'Interactive · Fractions', title: 'A fraction is equal parts of one whole', body: 'Split the bar into equal pieces, then tap to shade some. The bar, the circle and the number line all show the same fraction three ways. Try a few.', lumi: 'The bottom number, the denominator, is how many equal parts the whole is cut into. The top number, the numerator, is how many of them you shaded.' },
  { eyebrow: 'Predict', title: 'Cut each piece in half. What changes?', body: 'Below is one half shaded. Imagine cutting every piece into two. Before you reveal it: how much will be shaded then, compared with now?', lumi: 'Same shaded area, new name. One half and two quarters cover identical space on the line. That is what equivalent means.' },
  { eyebrow: 'Explore · The equivalence machine', title: 'One point, many names', body: 'Pick a fraction, then turn the dial to cut every piece into more parts. The shaded amount never moves on the number line, but its name keeps changing. Those are equivalent fractions.', lumi: 'Multiplying the top and bottom by the same number just re-cuts the same whole, so the value stays put.' },
  { eyebrow: 'Check', title: 'Spot the equivalent fraction', body: 'Which of these names the same amount as two thirds?', lumi: '' },
];

const PRED_OPTS = [
  { text: 'More than before', fb: 'Look closely at the number line. The shaded length is identical, so it cannot be more. We only renamed it.', good: false },
  { text: 'Exactly the same amount', fb: 'Yes. Cutting pieces smaller adds nothing and removes nothing. 1/2 and 2/4 mark the very same point.', good: true },
  { text: 'Less than before', fb: 'The pieces are smaller, but more of them are shaded. The total shaded length stays the same.', good: false },
];
const CHECK_OPTS = [
  { text: '4/6', correct: true,  fb: 'Correct. Multiply 2/3 top and bottom by 2 and you get 4/6, the same point on the line.' },
  { text: '3/4', correct: false, fb: 'Close in size, but 3/4 sits further along the line than 2/3. Not the same point.' },
  { text: '6/8', correct: false, fb: '6/8 simplifies to 3/4, not 2/3. Try the one that doubles both numbers in 2/3.' },
  { text: '2/6', correct: false, fb: '2/6 simplifies to 1/3, which is half of 2/3. Look for the one that doubles 2 and 3.' },
];
const BASES = [[1,2],[1,3],[2,3],[3,4],[2,5]];
const K_WORDS = { 1:'whole pieces', 2:'halves', 3:'thirds', 4:'quarters', 5:'fifths' };

function wedge(cx, cy, r, a0, a1) {
  const p0x = cx + r*Math.cos(a0), p0y = cy + r*Math.sin(a0);
  const p1x = cx + r*Math.cos(a1), p1y = cy + r*Math.sin(a1);
  return `M${cx} ${cy} L${p0x.toFixed(2)} ${p0y.toFixed(2)} A${r} ${r} 0 ${(a1-a0)>Math.PI?1:0} 1 ${p1x.toFixed(2)} ${p1y.toFixed(2)} Z`;
}
function svgNumLine(svgEl, parts, valNum, valDen, labelText) {
  if (!svgEl) return;
  const lx0=20, lw=480, ly=34;
  let t = `<line x1="${lx0}" y1="${ly}" x2="${lx0+lw}" y2="${ly}" stroke="#C9C5DD" stroke-width="2"/>`;
  for (let j = 0; j <= parts; j++) {
    const x = lx0 + (lw/parts)*j, big = j===0||j===parts;
    t += `<line x1="${x.toFixed(1)}" y1="${ly-(big?9:6)}" x2="${x.toFixed(1)}" y2="${ly+(big?9:6)}" stroke="${big?'#8983B8':'#C2BDD8'}" stroke-width="${big?2:1.4}"/>`;
  }
  t += `<text x="${lx0}" y="${ly+24}" fill="#8983B8" font-family="Space Mono,monospace" font-size="12" text-anchor="middle">0</text>`;
  t += `<text x="${lx0+lw}" y="${ly+24}" fill="#8983B8" font-family="Space Mono,monospace" font-size="12" text-anchor="middle">1</text>`;
  const mx = lx0 + lw*(valNum/valDen);
  t += `<line x1="${mx.toFixed(1)}" y1="${ly-16}" x2="${mx.toFixed(1)}" y2="${ly+10}" stroke="${AC}" stroke-width="2.5"/>`;
  t += `<circle cx="${mx.toFixed(1)}" cy="${ly-16}" r="6" fill="${AC}" stroke="#fff" stroke-width="2"/>`;
  if (labelText) t += `<text x="${mx.toFixed(1)}" y="${ly-25}" fill="${AC}" font-family="Space Grotesk,sans-serif" font-weight="700" font-size="12" text-anchor="middle">${labelText}</text>`;
  svgEl.innerHTML = t;
}

const FRACTION_POS_KEY = 'lumio_screen_pos_fraction_foundry';

export default function FractionFoundry({ onClose }) {
  const [screen, setScreen] = useState(() => {
    try {
      const pos = JSON.parse(localStorage.getItem(FRACTION_POS_KEY) || 'null');
      if (pos && Number.isInteger(pos.idx) && pos.idx < SCREENS.length && Date.now() - (pos.savedAt || 0) < 24 * 60 * 60 * 1000) {
        return pos.idx;
      }
    } catch (e) {}
    return 0;
  });
  const [d, setD] = useState(4);
  const [n, setN] = useState(3);
  const [predicted, setPredicted] = useState(null);
  const [baseN, setBaseN] = useState(1);
  const [baseD, setBaseD] = useState(2);
  const [k, setK] = useState(1);
  const [pick, setPick] = useState(null);

  useEffect(() => {
    try { localStorage.setItem(FRACTION_POS_KEY, JSON.stringify({ idx: screen, savedAt: Date.now() })); } catch (e) {}
  }, [screen]);

  const barRef  = useRef(null);
  const lineRef = useRef(null);
  const pieRef  = useRef(null);
  const predRef = useRef(null);
  const mbarRef = useRef(null);
  const mlineRef = useRef(null);

  // Draw bench (screen 0)
  useEffect(() => {
    if (screen !== 0) return;
    const bar = barRef.current;
    if (bar) {
      const x0=20, w=480, bh=64, by=18, seg=w/d; let s='';
      for (let j=0;j<d;j++) { const x=x0+j*seg; s+=`<rect x="${x.toFixed(1)}" y="${by}" width="${seg.toFixed(1)}" height="${bh}" rx="3" fill="${j<n?AC:'#EDEBF6'}" stroke="#FBFAF6" stroke-width="3"/>`; }
      s+=`<rect x="${x0}" y="${by}" width="${w}" height="${bh}" rx="4" fill="none" stroke="#CFCBE4" stroke-width="1.5"/>`;
      bar.innerHTML = s;
    }
    svgNumLine(lineRef.current, d, n, d, n+'/'+d);
    const pie = pieRef.current;
    if (pie) {
      const cx=80, cy=84, r=66, a0=-Math.PI/2; let p='';
      for (let j=0;j<d;j++) p+=`<path d="${wedge(cx,cy,r,a0+(2*Math.PI/d)*j,a0+(2*Math.PI/d)*(j+1))}" fill="${j<n?AC:'#EDEBF6'}" stroke="#FBFAF6" stroke-width="2.5"/>`;
      p+=`<circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="#CFCBE4" stroke-width="1.5"/>`;
      pie.innerHTML = p;
    }
  }); // eslint-disable-line react-hooks/exhaustive-deps

  // Draw predict (screen 1)
  useEffect(() => {
    if (screen !== 1) return;
    const svg = predRef.current; if (!svg) return;
    const x0=20, w=480;
    const row = (y,parts,sh) => { let r=''; const seg=w/parts; for(let j=0;j<parts;j++) r+=`<rect x="${(x0+j*seg).toFixed(1)}" y="${y}" width="${seg.toFixed(1)}" height="50" rx="3" fill="${j<sh?AC:'#EDEBF6'}" stroke="#FBFAF6" stroke-width="3"/>`;  r+=`<rect x="${x0}" y="${y}" width="${w}" height="50" rx="4" fill="none" stroke="#CFCBE4" stroke-width="1.5"/>`; return r; };
    const revealed = predicted !== null;
    let s='';
    s+=`<text x="${x0}" y="16" fill="#8983B8" font-family="Space Mono,monospace" font-size="12">Before — one half shaded</text>`;
    s+=row(24,2,1);
    if (revealed) {
      s+=`<text x="${x0}" y="104" fill="#8983B8" font-family="Space Mono,monospace" font-size="12">After — each piece cut in two (two quarters)</text>`;
      s+=row(112,4,2);
    } else {
      s+=`<text x="260" y="150" fill="#B7B2D0" font-family="Space Mono,monospace" font-size="13" text-anchor="middle">make a prediction to reveal</text>`;
    }
    const ly=210, lw=480;
    s+=`<line x1="${x0}" y1="${ly}" x2="${x0+lw}" y2="${ly}" stroke="#C9C5DD" stroke-width="2"/>`;
    for(let j=0;j<=4;j++){ const x=x0+(lw/4)*j, big=j===0||j===4; s+=`<line x1="${x}" y1="${ly-(big?9:6)}" x2="${x}" y2="${ly+(big?9:6)}" stroke="${big?'#8983B8':'#C2BDD8'}" stroke-width="${big?2:1.4}"/>`; }
    s+=`<text x="${x0}" y="${ly+22}" fill="#8983B8" font-family="Space Mono,monospace" font-size="12" text-anchor="middle">0</text>`;
    s+=`<text x="${x0+lw}" y="${ly+22}" fill="#8983B8" font-family="Space Mono,monospace" font-size="12" text-anchor="middle">1</text>`;
    const mx=x0+lw*0.5;
    s+=`<line x1="${mx}" y1="${ly-16}" x2="${mx}" y2="${ly+10}" stroke="${AC}" stroke-width="2.5"/>`;
    s+=`<circle cx="${mx}" cy="${ly-16}" r="6" fill="${AC}" stroke="#fff" stroke-width="2"/>`;
    s+=`<text x="${mx}" y="${ly-25}" fill="${AC}" font-family="Space Grotesk,sans-serif" font-weight="700" font-size="12" text-anchor="middle">${revealed?'1/2 = 2/4':'1/2'}</text>`;
    svg.innerHTML = s;
  }); // eslint-disable-line react-hooks/exhaustive-deps

  // Draw equivalence machine (screen 2)
  useEffect(() => {
    if (screen !== 2) return;
    const bar = mbarRef.current;
    if (bar) {
      const x0=20, w=480, bh=70, by=20, major=w/baseD; let s='';
      for (let mi=0;mi<baseD;mi++) { const mx2=x0+mi*major; for(let si=0;si<k;si++){ const sw=major/k; s+=`<rect x="${(mx2+si*sw).toFixed(2)}" y="${by}" width="${sw.toFixed(2)}" height="${bh}" fill="${mi<baseN?AC:'#EDEBF6'}" stroke="#FBFAF6" stroke-width="${k>1?2:0}"/>`; } }
      for (let mi=0;mi<=baseD;mi++){ const x=x0+mi*major; s+=`<line x1="${x.toFixed(1)}" y1="${by-4}" x2="${x.toFixed(1)}" y2="${by+bh+4}" stroke="#4B45A8" stroke-width="2"/>`; }
      s+=`<rect x="${x0}" y="${by}" width="${w}" height="${bh}" fill="none" stroke="#CFCBE4" stroke-width="1.5"/>`;
      bar.innerHTML = s;
    }
    const line = mlineRef.current;
    if (line) {
      const lx0=20, lw=480, ly=112; let t=`<line x1="${lx0}" y1="${ly}" x2="${lx0+lw}" y2="${ly}" stroke="#C9C5DD" stroke-width="2"/>`;
      t+=`<line x1="${lx0}" y1="${ly-9}" x2="${lx0}" y2="${ly+9}" stroke="#8983B8" stroke-width="2"/><line x1="${lx0+lw}" y1="${ly-9}" x2="${lx0+lw}" y2="${ly+9}" stroke="#8983B8" stroke-width="2"/>`;
      t+=`<text x="${lx0}" y="${ly+22}" fill="#8983B8" font-family="Space Mono,monospace" font-size="12" text-anchor="middle">0</text><text x="${lx0+lw}" y="${ly+22}" fill="#8983B8" font-family="Space Mono,monospace" font-size="12" text-anchor="middle">1</text>`;
      const mx=lx0+lw*(baseN/baseD);
      t+=`<line x1="${mx.toFixed(1)}" y1="${(ly-12-(k-1)*15-6).toFixed(0)}" x2="${mx.toFixed(1)}" y2="${ly+10}" stroke="${AC}" stroke-width="2.5"/>`;
      t+=`<circle cx="${mx.toFixed(1)}" cy="${ly}" r="6" fill="${AC}" stroke="#fff" stroke-width="2"/>`;
      for(let j=1;j<=k;j++){ const y=ly-12-(k-j)*15, active=j===k; t+=`<text x="${mx.toFixed(1)}" y="${y}" fill="${active?AC:'#B7B2D0'}" font-family="Space Grotesk,sans-serif" font-weight="700" font-size="${active?13:11.5}" text-anchor="middle">${baseN*j}/${baseD*j}</text>`; }
      line.innerHTML = t;
    }
  }); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBarClick = (e) => {
    const svg = barRef.current; if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width * 520;
    const seg = 480 / d;
    let idx = Math.floor((x - 20) / seg);
    idx = Math.max(0, Math.min(d - 1, idx));
    setN(idx + 1);
  };

  const sc = SCREENS[screen];
  const correctPicked = pick !== null && CHECK_OPTS[pick].correct;
  const predGated = screen === 1 && predicted === null;
  const checkGated = screen === 3 && !correctPicked;
  const gated = predGated || checkGated;
  const isLast = screen === 3;
  const nextLabel = isLast ? 'Start over ↺'
    : predGated ? 'Make a prediction'
    : checkGated ? 'Pick the match to go on'
    : 'Continue →';

  const goNext = () => {
    if (gated) return;
    if (isLast) { setScreen(0); setPredicted(null); setPick(null); setD(4); setN(3); setBaseN(1); setBaseD(2); setK(1); return; }
    setScreen(s => s + 1);
    setPick(null);
  };
  const goBack = () => { if (screen > 0) { setScreen(s => s - 1); setPick(null); } else onClose(); };

  const predFb = predicted !== null ? PRED_OPTS[predicted] : null;
  const checkFb = pick !== null ? CHECK_OPTS[pick] : null;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9000, background: `radial-gradient(1200px 600px at 50% -10%,#FBF8EF,${T.bg} 60%)`, display: 'flex', flexDirection: 'column', alignItems: 'center', overflowY: 'auto', fontFamily: T.hf }}>
      <div style={{ width: '100%', maxWidth: 900, minHeight: '100vh', display: 'flex', flexDirection: 'column', background: T.bg, boxShadow: '0 0 60px rgba(120,110,80,.10)' }}>

        {/* Header */}
        <div style={{ padding: '20px 30px 0', display: 'flex', alignItems: 'center', gap: 14 }}>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px 6px', color: T.dim, fontSize: 18 }}>×</button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <div style={{ width: 24, height: 24, borderRadius: 7, background: `linear-gradient(135deg,${AC2},${AC})` }} />
            <span style={{ fontFamily: T.fd, fontWeight: 700, fontSize: 16, color: T.text }}>Empirio</span>
          </div>
          <div style={{ flex: 1, display: 'flex', gap: 5 }}>
            {SCREENS.map((_, k2) => <div key={k2} style={{ flex: 1, height: 6, borderRadius: 99, background: k2 <= screen ? AC : T.fdim, transition: 'background .45s ease' }} />)}
          </div>
          <span style={{ fontFamily: T.fm, fontSize: 11, color: T.dim }}>{screen+1}/4</span>
        </div>
        <div style={{ padding: '9px 30px 4px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 17 }}>📐</span>
          <div style={{ fontFamily: T.fd, fontWeight: 600, fontSize: 14, color: '#6B6557' }}>Mathematics · Year 6 · Fractions</div>
        </div>

        {/* Screen content */}
        <div style={{ flex: 1, padding: '18px 30px 0', display: 'flex', flexDirection: 'column' }}>
          <span style={{ fontFamily: T.fm, fontSize: 11.5, letterSpacing: '0.1em', textTransform: 'uppercase', color: AC, fontWeight: 700 }}>{sc.eyebrow}</span>
          <h1 style={{ fontFamily: T.fd, fontWeight: 700, fontSize: 27, color: T.text, letterSpacing: '-0.02em', lineHeight: 1.15, margin: '11px 0 12px' }}>{sc.title}</h1>
          <p style={{ fontSize: 16, color: T.muted, lineHeight: 1.6, maxWidth: 660, margin: 0 }}>{sc.body}</p>

          {/* Screen 0: Concept bench */}
          {screen === 0 && (
            <>
              <div style={{ marginTop: 18, background: T.card, border: `1px solid ${T.border}`, borderRadius: 18, padding: '20px 22px', boxShadow: '0 6px 20px rgba(120,110,80,.07)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                  <span style={{ fontFamily: T.fm, fontSize: 11, color: '#6B7280' }}>Equal parts</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <button onClick={() => { const nd = Math.max(2, d-1); setD(nd); setN(prev => Math.min(prev, nd)); }} style={{ width:32,height:32,borderRadius:9,border:`1.5px solid ${T.inputBorder}`,background:T.inputBg,color:AC,fontSize:18,fontWeight:700,cursor:'pointer',lineHeight:1 }}>−</button>
                    <span style={{ fontFamily: T.fd, fontWeight: 700, fontSize: 18, color: T.text, minWidth: 24, textAlign: 'center' }}>{d}</span>
                    <button onClick={() => setD(prev => Math.min(12, prev+1))} style={{ width:32,height:32,borderRadius:9,border:`1.5px solid ${T.inputBorder}`,background:T.inputBg,color:AC,fontSize:18,fontWeight:700,cursor:'pointer',lineHeight:1 }}>+</button>
                  </div>
                  <span style={{ fontFamily: T.fm, fontSize: 11, color: T.dim, marginLeft: 'auto' }}>tap the bar to shade</span>
                </div>
                <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'center' }}>
                  <div style={{ flex: 1, minWidth: 320 }}>
                    <svg ref={barRef} viewBox="0 0 520 104" style={{ display: 'block', width: '100%', cursor: 'pointer', touchAction: 'none' }} onClick={handleBarClick} />
                    <svg ref={lineRef} viewBox="0 0 520 70" style={{ display: 'block', width: '100%', marginTop: 4 }} />
                  </div>
                  <svg ref={pieRef} viewBox="0 0 160 168" style={{ display: 'block', width: 148, flex: '0 0 148px' }} />
                </div>
              </div>
              <div style={{ marginTop: 13, display: 'flex', gap: 11 }}>
                <div style={{ flex:1,background:T.inputBg,border:`1px solid #E2DDF2`,borderRadius:13,padding:'13px 15px',textAlign:'center' }}>
                  <div style={{ fontFamily:T.fm,fontSize:11,color:'#8983B8' }}>Fraction</div>
                  <div style={{ fontFamily:T.fd,fontWeight:700,fontSize:22,color:AC }}>{n}/{d}</div>
                </div>
                <div style={{ flex:1,background:T.card,border:`1px solid ${T.border}`,borderRadius:13,padding:'13px 15px',textAlign:'center' }}>
                  <div style={{ fontFamily:T.fm,fontSize:11,color:T.dim }}>Decimal</div>
                  <div style={{ fontFamily:T.fd,fontWeight:700,fontSize:22,color:T.text }}>{(n/d).toFixed(2)}</div>
                </div>
                <div style={{ flex:1,background:T.card,border:`1px solid ${T.border}`,borderRadius:13,padding:'13px 15px',textAlign:'center' }}>
                  <div style={{ fontFamily:T.fm,fontSize:11,color:T.dim }}>Percent</div>
                  <div style={{ fontFamily:T.fd,fontWeight:700,fontSize:22,color:T.text }}>{Math.round(n/d*100)}%</div>
                </div>
              </div>
            </>
          )}

          {/* Screen 1: Predict */}
          {screen === 1 && (
            <>
              <div style={{ marginTop: 18, background: T.card, border: `1px solid ${T.border}`, borderRadius: 18, padding: '20px 22px', boxShadow: '0 6px 20px rgba(120,110,80,.07)' }}>
                <svg ref={predRef} viewBox="0 0 520 232" style={{ display: 'block', width: '100%' }} />
              </div>
              <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {PRED_OPTS.map((o, oi) => {
                  const sel = predicted === oi;
                  return <button key={oi} onClick={() => setPredicted(oi)} style={{ textAlign:'left',background:sel?T.inputBg:T.card,border:`1.5px solid ${sel?AC:T.border}`,borderRadius:13,padding:'15px 17px',fontFamily:T.hf,fontWeight:600,fontSize:14.5,color:'#2B2F36',cursor:'pointer',transition:'all .18s ease',boxShadow:'0 2px 8px rgba(120,110,80,.05)' }}>{o.text}</button>;
                })}
              </div>
              {predFb && (
                <div style={{ marginTop:14,background:predFb.good?'#EEF6F0':'#FBF6EC',border:`1px solid ${predFb.good?'#CFE7D4':'#F0E2C0'}`,borderRadius:13,padding:'14px 16px',display:'flex',gap:11 }}>
                  <div style={{ width:24,height:24,flexShrink:0,borderRadius:'50%',background:`radial-gradient(circle at 35% 30%,${AC2},${AC})` }} />
                  <p style={{ fontSize:13.5,color:'#3D4550',lineHeight:1.55,margin:0 }}>{predFb.fb}</p>
                </div>
              )}
            </>
          )}

          {/* Screen 2: Equivalence machine */}
          {screen === 2 && (
            <>
              <div style={{ marginTop: 18, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {BASES.map(([bn, bd]) => {
                  const active = baseN===bn && baseD===bd;
                  return <button key={`${bn}/${bd}`} onClick={() => { setBaseN(bn); setBaseD(bd); setK(1); }} style={{ background:active?AC:T.card,border:`1.5px solid ${active?AC:'#DAD5E8'}`,color:active?'#fff':T.text,borderRadius:11,padding:'9px 15px',fontFamily:T.fd,fontWeight:700,fontSize:15,cursor:'pointer',transition:'all .15s ease' }}>{bn}/{bd}</button>;
                })}
              </div>
              <div style={{ marginTop: 13, background: T.card, border: `1px solid ${T.border}`, borderRadius: 18, padding: '20px 22px', boxShadow: '0 6px 20px rgba(120,110,80,.07)' }}>
                <svg ref={mbarRef} viewBox="0 0 520 108" style={{ display: 'block', width: '100%' }} />
                <svg ref={mlineRef} viewBox="0 0 520 132" style={{ display: 'block', width: '100%', marginTop: 2 }} />
                <div style={{ marginTop:16,display:'flex',alignItems:'center',gap:14,paddingTop:16,borderTop:`1px solid #F0ECE2` }}>
                  <span style={{ fontFamily:T.fm,fontSize:12,color:'#6B7280' }}>Cut each piece into</span>
                  <div style={{ display:'flex',alignItems:'center',gap:8 }}>
                    <button onClick={() => setK(v => Math.max(1,v-1))} style={{ width:34,height:34,borderRadius:9,border:`1.5px solid ${T.inputBorder}`,background:T.inputBg,color:AC,fontSize:19,fontWeight:700,cursor:'pointer',lineHeight:1 }}>−</button>
                    <span style={{ fontFamily:T.fd,fontWeight:700,fontSize:20,color:T.text,minWidth:26,textAlign:'center' }}>{k}</span>
                    <button onClick={() => setK(v => Math.min(5,v+1))} style={{ width:34,height:34,borderRadius:9,border:`1.5px solid ${T.inputBorder}`,background:T.inputBg,color:AC,fontSize:19,fontWeight:700,cursor:'pointer',lineHeight:1 }}>+</button>
                  </div>
                  <span style={{ fontFamily:T.fm,fontSize:12,color:'#6B7280' }}>{K_WORDS[k]||''}</span>
                </div>
              </div>
              <div style={{ marginTop:13,background:T.inputBg,border:`1px solid #E2DDF2`,borderRadius:14,padding:'15px 18px',display:'flex',alignItems:'center',gap:12,flexWrap:'wrap' }}>
                <span style={{ fontFamily:T.fm,fontSize:11,color:'#8983B8',textTransform:'uppercase',letterSpacing:'0.08em' }}>Equivalent</span>
                <div style={{ display:'flex',alignItems:'center',gap:10,flexWrap:'wrap' }}>
                  {Array.from({length:k},(_,j)=>j+1).map(j => {
                    const active = j===k;
                    return <span key={j} style={{ fontFamily:T.fd,fontWeight:700,fontSize:active?22:19,color:active?AC:'#9B95C4',opacity:active?1:0.65,transition:'all .25s ease' }}>{j>1?'= ':''}{baseN*j}/{baseD*j}</span>;
                  })}
                </div>
              </div>
            </>
          )}

          {/* Screen 3: Check */}
          {screen === 3 && (
            <>
              <div style={{ marginTop: 22, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {CHECK_OPTS.map((o, oi) => {
                  const sel = pick === oi;
                  const border = sel ? (o.correct ? AC : '#DC2626') : T.border;
                  return <button key={oi} onClick={() => setPick(oi)} style={{ textAlign:'left',background:T.card,border:`1.5px solid ${border}`,borderRadius:13,padding:'15px 18px',fontFamily:T.fd,fontWeight:700,fontSize:17,color:'#2B2F36',cursor:'pointer',transition:'all .18s ease',boxShadow:'0 2px 8px rgba(120,110,80,.05)' }}>{o.text}</button>;
                })}
              </div>
              {checkFb && (
                <div style={{ marginTop:14,background:checkFb.correct?'#EEF6F0':'#FBEEEC',border:`1px solid ${checkFb.correct?'#CFE7D4':'#F3D2CC'}`,borderRadius:13,padding:'14px 16px',display:'flex',gap:11 }}>
                  <div style={{ width:24,height:24,flexShrink:0,borderRadius:'50%',background:`radial-gradient(circle at 35% 30%,${AC2},${AC})` }} />
                  <p style={{ fontSize:13.5,color:'#3D4550',lineHeight:1.55,margin:0 }}>{checkFb.fb}</p>
                </div>
              )}
            </>
          )}

          <div style={{ flex: 1, minHeight: 14 }} />

          {/* Lumi */}
          {sc.lumi && (
            <div style={{ marginTop:20,background:T.lumiCard,border:`1px solid ${T.lumiBorder}`,borderRadius:15,borderTopLeftRadius:5,padding:'14px 16px',display:'flex',gap:12 }}>
              <div style={{ width:28,height:28,flexShrink:0,borderRadius:'50%',background:`radial-gradient(circle at 35% 30%,${AC2},${AC})` }} />
              <div>
                <div style={{ display:'flex',alignItems:'center',gap:6,marginBottom:3 }}>
                  <span style={{ fontWeight:700,fontSize:13,color:T.text }}>Lumi</span>
                  <span style={{ width:6,height:6,borderRadius:'50%',background:AC,display:'inline-block' }} />
                </div>
                <p style={{ fontSize:13,color:T.muted,lineHeight:1.5,margin:0 }}>{sc.lumi}</p>
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div style={{ padding:'18px 30px 26px',display:'flex',gap:10,position:'sticky',bottom:0,background:`linear-gradient(transparent,${T.bg} 24%)` }}>
          <button onClick={goBack} style={{ background:'#EFEDE5',color:'#6B7280',border:'none',borderRadius:13,padding:'15px 20px',fontFamily:T.hf,fontWeight:700,fontSize:14,cursor:'pointer' }}>←</button>
          <button onClick={goNext} style={{ flex:1,background:gated?'#C7C3E6':`linear-gradient(135deg,${AC2},${AC})`,color:'#fff',border:'none',borderRadius:13,padding:16,fontFamily:T.hf,fontWeight:800,fontSize:15,cursor:gated?'default':'pointer',boxShadow:gated?'none':'0 6px 18px rgba(108,99,255,.22)' }}>{nextLabel}</button>
        </div>

      </div>
    </div>
  );
}
